import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components/macro';

import DinteroLogo from './dintero-dark.svg';
import BackgroundAnimatedGrid from './BackgroundAnimatedGrid';
import Login from './Login';
import ForgotPassword from './ForgotPassword';
import VerifyMfa from './VerifyMfa';
import VerifyForgotPassword from './VerifyForgotPassword';
import { colors, distances } from '../../../styles/constants';
import { CognitoUser } from 'amazon-cognito-identity-js';
import useWindowSize from '../../../util/useWindowSize';
import { MfaType, SelectMfaTypeOptions } from '../../types';
import SelectMfaType from './SelectMfaType';

const LoginPage = () => {
    const [loginView, setLoginView] = useState<'login' | 'forgotPassword' | 'verifyForgotPassword' | 'mfaRequired' | 'selectMfaType'>(
        'login'
    );
    const [shake, setShake] = useState<boolean>(false);

    const [username, setUsername] = useState<string>('');
    const [cognitoUser, setCognitoUser] = useState<CognitoUser | undefined>();
    const [mfaType, setMfaType] = useState<MfaType | undefined>();
    const [selectMfaTypeOptions, setSelectMfaTypeOptions] = useState<undefined | SelectMfaTypeOptions>();
    if (loginView === 'login') {
        return (
            <LoginPageWrapper shake={shake} setShake={setShake}>
                <Login
                    username={username}
                    setForgotPassword={(username: string) => {
                        setUsername(username);
                        setLoginView('forgotPassword');
                        setCognitoUser(undefined);
                    }}
                    setMfaRequired={(cognitoUser: CognitoUser, mfaType) => {
                        setCognitoUser(cognitoUser);
                        setLoginView('mfaRequired');
                        setMfaType(mfaType);
                    }}
                    setSelectMfaRequired={(cognitoUser: CognitoUser, options) => {
                        setCognitoUser(cognitoUser);
                        setSelectMfaTypeOptions(options);
                        setLoginView('selectMfaType');
                    }}
                    setShake={setShake}
                />
            </LoginPageWrapper>
        );
    }
    if (loginView === 'forgotPassword') {
        return (
            <LoginPageWrapper shake={shake} setShake={setShake}>
                <ForgotPassword
                    username={username}
                    setSubmitUsername={(username: string) => {
                        setUsername(username);
                        setLoginView('verifyForgotPassword');
                        setCognitoUser(undefined);
                    }}
                    setBack={(username: string) => {
                        setUsername(username);
                        setLoginView('login');
                        setCognitoUser(undefined);
                    }}
                    setShake={setShake}
                />
            </LoginPageWrapper>
        );
    }
    if (loginView === 'mfaRequired' && cognitoUser) {
        return (
            <LoginPageWrapper shake={shake} setShake={setShake}>
                <VerifyMfa
                    cognitoUser={cognitoUser}
                    setBack={() => {
                        setLoginView('login');
                        setCognitoUser(undefined);
                    }}
                    setShake={setShake}
                    mfaType={mfaType || 'SMS_MFA'}
                />
            </LoginPageWrapper>
        );
    }
    if (loginView === 'selectMfaType' && cognitoUser && selectMfaTypeOptions) {
        return (
            <LoginPageWrapper shake={shake} setShake={setShake}>
                <SelectMfaType
                    cognitoUser={cognitoUser}
                    setBack={() => {
                        setLoginView('login');
                        setCognitoUser(undefined);
                    }}
                    setMfaRequired={(cognitoUser: CognitoUser, mfaType) => {
                        setCognitoUser(cognitoUser);
                        setLoginView('mfaRequired');
                        setMfaType(mfaType);
                    }}
                    setShake={setShake}
                    selectMfaTypeOptions={selectMfaTypeOptions}
                />
            </LoginPageWrapper>
        );
    }
    return (
        <LoginPageWrapper shake={shake} setShake={setShake}>
            <VerifyForgotPassword
                username={username}
                setBack={() => {
                    setLoginView('login');
                    setCognitoUser(undefined);
                }}
                setMfaRequired={(cognitoUser: CognitoUser, mfaType) => {
                    setCognitoUser(cognitoUser);
                    setLoginView('mfaRequired');
                    setMfaType(mfaType);
                }}
                setShake={setShake}
                setSelectMfaRequired={(cognitoUser: CognitoUser, options) => {
                    setCognitoUser(cognitoUser);
                    setSelectMfaTypeOptions(options);
                    setLoginView('selectMfaType');
                }}
            />
        </LoginPageWrapper>
    );
};

type Cover = {
    width: number;
    height: number;
    offsetY: number;
    offsetX: number;
};

const calculateCover = (windowWidth: number, windowHeight: number, aspectRatio: number): Cover => {
    // emulate css cover where the image fits to the widow but keeps aspect ratio intact
    const heightFromWidth = windowWidth / aspectRatio;
    if (heightFromWidth > windowHeight) {
        return {
            width: windowWidth,
            height: heightFromWidth,
            offsetY: heightFromWidth - windowHeight,
            offsetX: 0,
        };
    }
    const widthFromHeight = windowHeight * aspectRatio;
    return {
        width: widthFromHeight,
        height: windowHeight,
        offsetY: 0,
        offsetX: widthFromHeight - windowWidth,
    };
};

const LoginPageWrapper = ({ children, shake, setShake }: {
    children: React.ReactNode;
    shake: boolean;
    setShake: (value: boolean) => void;
}) => {
    const { width, height } = useWindowSize();
    const cover = calculateCover(width, height, 1400 / 800);
    return (
        <>
            <BackgroundWrapper cover={cover}>
                <BackgroundAnimatedGrid />
            </BackgroundWrapper>
            <LoginContainer onAnimationEnd={() => setShake(false)} className={shake ? 'shake' : ''}>
                <LogoWrapper>
                    <img src={DinteroLogo} alt="Dintero Logo" />
                </LogoWrapper>
                {children}
            </LoginContainer>
        </>
    );
};

const shake = keyframes`
  10%, 90% {
    transform: translateX(-2px);
  }
  20%, 80% {
    transform: translateX(4px);
  }

  30%, 50%, 70% {
    transform: translateX(-6px);
  }

  40%, 60% {
    transform: translateX(8px);
}`;

const LoginContainer = styled.div`
    z-index: 1;
    width: 600px;
    margin: 25vh auto 0;
    padding: 50px;
    box-shadow: rgb(0 0 0 / 20%) 5px 5px 20px;
    background-color: ${colors.background};
    border-radius: ${distances.tiny};
    transform-origin: center;

    &.shake {
        animation: ${shake} 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    }

    @media (max-width: 768px) {
        width: 100%;
        height: 100vh;
        margin: 0;
        bottom: 0;
    }
`;

const draw = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

const BackgroundWrapper = styled.div<{ cover: Cover }>`
    z-index: 0;
    position: fixed;
    top: -${(props) => props.cover.offsetY / 2}px;
    bottom: -${(props) => props.cover.offsetY / 2}px;
    left: -${(props) => props.cover.offsetX / 2}px;
    right: -${(props) => props.cover.offsetX / 2}px;
    > svg {
        height: ${(props) => props.cover.height}px;
        width: ${(props) => props.cover.width}px;
        path {
            stroke-dasharray: 2000;
            stroke-dashoffset: 2000;
            animation: ${draw} 7s linear forwards;
        }
        polyline {
            stroke-dasharray: 2000;
            stroke-dashoffset: 2000;
            animation: ${draw} 10s linear forwards;
        }
    }

    @media (max-width: 768px) {
        display: none;
    }
`;

const LogoWrapper = styled.div`
    margin-bottom: 30px;
`;

export default LoginPage;
